import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Switch,
    FormControlLabel,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Axios from "axios";
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import Loading from "../../components/Loading";
import ConfirmDialog from "../../components/ConfirmDialog";
import { canUseDevTools } from "../../utils/devToolsAccess";

const PrepPrograms = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const signOut = useSignOut();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);
    const [pendingUuid, setPendingUuid] = useState(null); // archive/restore request in flight
    const confirmRef = useRef();

    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    };

    const fetchPrograms = async () => {
        setLoading(true);
        try {
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/prep-programs`, {
                headers: { Authorization: getAuthHeader() },
            });
            setPrograms(res.data);
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                console.error("List prep programs error:", error);
                setPrograms([]);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        localStorage.setItem("selectedMenu", "prepPrograms");
        fetchPrograms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const doDelete = async (uuid, name) => {
        try {
            await Axios.delete(`${process.env.REACT_APP_URL}/api/v1/prep-programs/${uuid}`, {
                headers: { Authorization: getAuthHeader() },
            });
            setPrograms((prev) => prev.filter((p) => p.uuid !== uuid));
            enqueueSnackbar(name ? `Deleted “${name}”` : "Program deleted", { variant: "success" });
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                console.error("Delete prep program error:", error);
                enqueueSnackbar(error.response?.data?.error || "Failed to delete program", { variant: "error" });
            }
        }
    };

    // Archive = hide from the list (its own archived_at flag, independent of is_active). The
    // crons also skip archived programs, so archiving a finished program stops its reports /
    // homework too — but pausing automation via the editor's Active toggle does NOT hide it.
    const setArchived = async (p, archived) => {
        setPendingUuid(p.uuid); // disable the row's action while the request is in flight
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/prep-programs/${p.uuid}/archive`,
                { archived },
                { headers: { Authorization: getAuthHeader() } },
            );
            setPrograms((prev) => prev.map((x) => (x.uuid === p.uuid ? { ...x, archived_at: archived ? new Date().toISOString() : null } : x)));
            enqueueSnackbar(
                archived ? `Archived “${p.name}” — reports & homework paused` : `Restored “${p.name}”`,
                { variant: "success" },
            );
        } catch (error) {
            if (error.response?.status === 401) handleLogout();
            else {
                console.error("Archive prep program error:", error);
                enqueueSnackbar(error.response?.data?.error || "Failed to update program", { variant: "error" });
            }
        } finally {
            setPendingUuid(null);
        }
    };

    // Archiving stops the program's recurring reports + homework auto-release, so confirm it
    // (like Delete). Restoring needs no confirmation.
    const confirmArchive = (p) => {
        confirmRef.current.open({
            title: "Archive program?",
            message: `Archive "${p.name}"? It will be hidden from the list and its recurring reports and homework auto-release will stop. You can restore it anytime.`,
            confirmLabel: "Archive",
            onConfirm: () => setArchived(p, true),
        });
    };

    const confirmDelete = (p) => {
        confirmRef.current.open({
            title: "Delete program?",
            message: `Delete "${p.name}"? It will be removed from the list along with its courses, tests, and homework configuration.`,
            confirmLabel: "Delete",
            onConfirm: () => doDelete(p.uuid, p.name),
        });
    };

    // Distinct linked courses for a program (the same course can back several
    // program-courses when combined across roles/subjects).
    const linkedCourses = (p) => {
        const seen = new Set();
        const out = [];
        (p.prep_program_courses || []).forEach((pc) => {
            const c = pc.course;
            if (c && c.uuid && !seen.has(c.uuid)) {
                seen.add(c.uuid);
                out.push(c);
            }
        });
        return out;
    };
    const openClassroom = (uuid) => window.open(`/classrooms/${uuid}/home`, "_blank", "noopener");

    if (loading) return <Loading />;

    const archivedCount = programs.filter((p) => p.archived_at).length;
    const visible = showArchived ? programs : programs.filter((p) => !p.archived_at);

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
                <Typography variant="h4" fontWeight="bold">
                    Test Course Management
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                    <FormControlLabel
                        control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
                        label={`Show archived${archivedCount ? ` (${archivedCount})` : ""}`}
                    />
                    <Button
                        variant="outlined"
                        color="info"
                        startIcon={<MenuBookIcon />}
                        onClick={() =>
                            window.open(
                                `${process.env.PUBLIC_URL}/exam-prep-guide-kr.html`,
                                "_blank",
                                "noopener,noreferrer"
                            )
                        }
                    >
                        안내서
                    </Button>
                    {canUseDevTools() && (
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={() => navigate("/admin/prepPrograms/dev")}
                        >
                            Dev Tools
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/admin/prepPrograms/new")}
                    >
                        New Program
                    </Button>
                </Box>
            </Box>

            {programs.length === 0 ? (
                <Typography color={colors.grey[300]}>No prep programs yet.</Typography>
            ) : visible.length === 0 ? (
                <Typography color={colors.grey[300]}>
                    All programs are archived — turn on “Show archived” to see them.
                </Typography>
            ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600] }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Linked Courses</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((p) => {
                                const courses = linkedCourses(p);
                                return (
                                <TableRow key={p.uuid} hover>
                                    <TableCell>{p.name}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={p.program_type || "—"} />
                                    </TableCell>
                                    <TableCell>
                                        {courses.length === 0 ? "—" : (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                {courses.map((c) => (
                                                    <Chip
                                                        key={c.uuid}
                                                        size="small"
                                                        variant="outlined"
                                                        clickable
                                                        label={c.course_title}
                                                        onClick={() => openClassroom(c.uuid)}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {p.archived_at ? (
                                            <Chip size="small" variant="outlined" label="Archived" />
                                        ) : p.is_active ? (
                                            <Chip size="small" color="success" label="Active" />
                                        ) : (
                                            <Chip size="small" label="Paused" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            startIcon={<BarChartIcon />}
                                            onClick={() => navigate(`/admin/prepPrograms/${p.uuid}/dashboard`)}
                                        >
                                            Dashboard
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => navigate(`/admin/prepPrograms/${p.uuid}/edit`)}
                                        >
                                            Edit
                                        </Button>
                                        {p.archived_at ? (
                                            <Button
                                                size="small"
                                                color="success"
                                                startIcon={<UnarchiveIcon />}
                                                disabled={pendingUuid === p.uuid}
                                                onClick={() => setArchived(p, false)}
                                            >
                                                Restore
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                startIcon={<ArchiveIcon />}
                                                disabled={pendingUuid === p.uuid}
                                                onClick={() => confirmArchive(p)}
                                            >
                                                Archive
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => confirmDelete(p)}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ConfirmDialog ref={confirmRef} />
        </Box>
    );
};

export default PrepPrograms;
